/**
 * Orders Resource Client
 * Axios-based API client for order endpoints
 */

import { Order, OrderLineItem } from '../utils/order';

import { ApiClient } from './apiClient';

export class OrdersResource {
    constructor(private apiClient: ApiClient) { }

    /**
     * Fetch order by ID
     */
    async getOrder(orderId: string): Promise<Order> {
        const response = await this.apiClient.get<{ order: Order } | Order>(`/api/v1/orders/${orderId}`);

        // Handle both response formats: { success, order } and { order }
        if (typeof response === 'object' && response !== null && 'success' in response && response.success === false) {
            throw new Error((response as any).error || 'Failed to fetch order');
        }

        // If response has success property, use response.order
        // Otherwise, assume the response itself is the order data or has order property
        const orderData = typeof response === 'object' && response !== null && 'order' in response
            ? (response as { order: Order }).order
            : response;

        if (!orderData) {
            throw new Error('Order not found');
        }

        return orderData;
    }

    /**
     * Create a new order
     */
    async createOrder(checkoutSessionId: string): Promise<{ success: boolean; order?: Order; error?: string }> {
        return this.apiClient.post('/api/v1/orders', {
            checkoutSessionId,
        });
    }

    /**
     * Update order status
     */
    async updateOrderStatus(orderId: string, status: string): Promise<{ success: boolean; error?: string }> {
        return this.apiClient.patch(`/api/v1/orders/${orderId}/status`, {
            status,
        });
    }

    /**
     * Add items to an order
     */
    async addOrderItems(orderId: string, items: Omit<OrderLineItem, 'id'>[]): Promise<{ success: boolean; error?: string }> {
        return this.apiClient.post(`/api/v1/orders/${orderId}/items`, {
            items,
        });
    }

    /**
     * Get order line items
     */
    async getOrderLineItems(orderId: string): Promise<OrderLineItem[]> {
        const order = await this.getOrder(orderId);
        return order.items || [];
    }
}
